import type JSZip from 'jszip';
import { decodeXml, PACKAGE_REL_NS, parseXml, type XmlNode } from './xml';

const RELATIONSHIPS_PART = /^(.*\/)?_rels\/([^/]+)\.rels$/;

/** The part a relationships part belongs to, or null for the package-level `_rels/.rels`. */
function ownerOf(relationshipsPart: string): string | null {
  const match = RELATIONSHIPS_PART.exec(relationshipsPart);
  return match ? `${match[1] ?? ''}${match[2] ?? ''}` : null;
}

/** Resolve a package URI without filesystem or network access. */
export function resolveTarget(source: string, target: string): string | null {
  try {
    const withoutFragment = target.split('#')[0] ?? '';
    if (!withoutFragment) return source || null;
    const decoded = decodeURIComponent(withoutFragment);
    if (decoded.includes('\\') || decoded.includes('\0') || /^[a-z][a-z\d+.-]*:/i.test(decoded)) return null;
    const path = decoded.startsWith('/') ? [] : source.split('/').slice(0, -1);
    for (const segment of decoded.replace(/^\//, '').split('/')) {
      if (segment === '..') { if (!path.length) return null; path.pop(); }
      else if (segment && segment !== '.') path.push(segment);
    }
    return path.join('/');
  } catch { return null; }
}

/** Internal targets of a relationships part, or null when it cannot be read as one. */
async function internalTargets(archive: JSZip, relationshipsPart: string, owner: string): Promise<string[] | null> {
  let root: XmlNode;
  try {
    const entry = archive.file(relationshipsPart);
    if (!entry) return null;
    root = parseXml(decodeXml(await entry.async('uint8array')));
  } catch { return null; }
  if (root.uri !== PACKAGE_REL_NS || root.local !== 'Relationships') return null;
  const targets: string[] = [];
  for (const relation of root.children) {
    if (relation.attributes['{}TargetMode'] === 'External') continue;
    const resolved = resolveTarget(owner, relation.attributes['{}Target'] ?? '');
    if (!resolved) return null;
    targets.push(resolved);
  }
  return targets;
}

/**
 * Removes the relationships of a part the package no longer contains, with the
 * media only they referenced.
 *
 * Why: SuperDoc 2.10.1 exports this shape after a header is detached from its
 * section and its part deleted (`headerFooters.refs.clear` + `parts.delete`):
 * `word/header1.xml` is gone, `word/_rels/header1.xml.rels` and its image stay.
 * The gate refused the package, so the lawyer who had asked to remove the
 * letterhead could no longer save, download or replace the document
 * (production, 2026-10-08). Relationships without a source describe nothing: no
 * document content is reachable through them.
 *
 * Left untouched, so the gate still judges them: an empty placeholder (the
 * tolerated `footnotes.xml.rels`), relationships that cannot be read, and
 * relationships whose own target is missing. A media is dropped only when no
 * remaining relationships part references it: left in place, the letterhead
 * the lawyer removed would travel on, unreachable, in every later export.
 *
 * @param archive - The opened package; orphan entries are removed from it
 * @param names - Entry names of the package; kept in step with `archive`
 * @returns One record per removed relationships part, with its relationship count
 */
export async function repairOrphanRelationships(
  archive: JSZip,
  names: Set<string>,
): Promise<Array<{ part: string; count: number }>> {
  const orphans = new Map<string, string[]>();
  for (const part of names) {
    const owner = ownerOf(part);
    if (owner === null || names.has(owner)) continue;
    const targets = await internalTargets(archive, part, owner);
    if (!targets?.length || targets.some((target) => !names.has(target))) continue;
    orphans.set(part, targets);
  }
  if (!orphans.size) return [];

  // What the parts still in the package reference. One unreadable survivor
  // means no media can be proven unused: keep them all, the gate will refuse it.
  const stillReferenced = new Set<string>();
  let survivorsReadable = true;
  for (const part of names) {
    if (!part.endsWith('.rels') || orphans.has(part)) continue;
    // `_rels/.rels` has no owning part: its targets resolve from the package root.
    const targets = await internalTargets(archive, part, ownerOf(part) ?? '');
    if (targets === null) { survivorsReadable = false; break; }
    for (const target of targets) stillReferenced.add(target);
  }

  const repairs: Array<{ part: string; count: number }> = [];
  for (const [part, targets] of orphans) {
    archive.remove(part);
    names.delete(part);
    repairs.push({ part, count: targets.length });
    if (!survivorsReadable) continue;
    for (const target of targets) {
      if (!/(^|\/)media\//.test(target) || stillReferenced.has(target) || !names.has(target)) continue;
      archive.remove(target);
      names.delete(target);
    }
  }
  return repairs;
}
